import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { User as UserIcon, ShieldCheck } from 'lucide-react'

import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useUserStore } from '@caradasstore/UserStore'
import { extractApiError, FormModal, FormModalFooter } from '@components/branded-modal'
import { AdasBox, AdasFormControl, AdasFormLabel, AdasRadioGroup, AdasSelect, AdasTextField } from '@components/wrapper-components'
import { AddExistingUser, createNewUser, deleteUser, getClientUserQuota, getClients, updateUser } from '../../../api/api'
import { checkPermission, getRole, phoneNumberFormat, validations } from '../../../utils/utils'
import { BUTTONS_DISPLAY_LABEL, GENERIC_MESSAGES, TEXT_CONSTANTS } from '../../../constants'
import { ConfirmModal } from '@components/common'
import { Logger } from '../../../logger'

const ChallengeModal = ({ open, handleClose, handleContinue }) => {
  const [code, setCode] = useState('')

  return (
    // CAR-4050: the standard pop-up.
    <FormModal
      open={open}
      onClose={handleClose}
      icon={ShieldCheck}
      title='System Admin Code Required'
      width='max-w-[480px]'
      footer={
        <FormModalFooter onCancel={handleClose} onConfirm={() => handleContinue(code)} confirmLabel='Continue' confirmDisabled={!code} />
      }
    >
      <p className='mb-4 text-[13px] leading-5 text-[#334155]'>
        A system admin code is required to grant privileged access to all user accounts.
      </p>
      <AdasTextField
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        name='Admin Code'
        id='adminCodeInput'
        label='Admin Code'
        variant='outlined'
        fullWidth={true}
        InputLabelProps={{
          shrink: true
        }}
      />
    </FormModal>
  )
}

export const UserForm = (props) => {
  const {
    open = true,
    userData,
    close,
    onSaveSuccess,
    isEdit = false,
    global = false,
    isAddUser = false,
    allowDelete = false,
    onDeleteSuccess,
    // 'client' (a client's Client users tab): always a client user of the client in
    // userData.client_id, so neither the role nor the client is asked for (CAR-3869). Left
    // unset (Team & roles), every role and the client dropdown are offered.
    userKind,
    // The center's body shops, when the page already has them (Team & roles loads them on
    // arrival), so the Client dropdown is ready the moment the Client role is picked.
    clients: prefetchedClients
  } = props
  const isClientForm = userKind === 'client'
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const currentUser = useUserStore((store) => store.currentUser)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const [user, setUser] = useState(userData)
  const [apiError, setApiError] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isCreateNewUser, setIsCreateNewUser] = useState(true)
  const [showModal, setShowModal] = useState(false)
  // The center's body shops, for linking a client user to the one it acts for.
  const [fetchedClients, setFetchedClients] = useState([])
  const clients = prefetchedClients || fetchedClients
  const [touched, setTouched] = useState({
    name: false,
    email: false,
    phone: false,
    role: false
  })
  const [valid, setValid] = useState({
    name: validations.name(userData.name),
    email: validations.email(userData.email),
    phone: validations.phone(userData.phone)
  })

  const handleInputChange = useCallback((e) => {
    const name = e.target.name
    const value = name === 'phone' ? phoneNumberFormat(e.target.value) : e.target.value
    setUser((prev) => {
      const user = {
        ...prev,
        [name]: value
      }
      return user
    })
    setTouched((prev) => {
      return {
        ...prev,
        [name]: true
      }
    })
    setValid((prev) => {
      const valid = {
        ...prev
      }
      if (validations[name]) {
        valid[name] = validations[name](value)
      }
      return valid
    })
  }, [])

  const phoneNumberDisplay = useCallback(() => {
    if (user.phone) {
      return phoneNumberFormat(user.phone)
    }
  }, [user?.phone])

  const handleSetActive = useCallback((isActive) => {
    setUser((prev) => ({ ...prev, is_active: isActive }))
  }, [])

  const handleSetRole = useCallback((role) => {
    setTouched((prev) => ({ ...prev, role: true }))
    setUser((prev) => ({
      ...prev,
      is_admin: role === 'Admin',
      is_owner: role === 'Owner',
      is_manager: role === 'Manager',
      is_client_user: role === 'Client',
      // The backend clears the body shop for every other role (and a DB check refuses it), so
      // the form drops it too rather than sending a value the server will reject.
      ...(role === 'Client' ? {} : { client_id: null })
    }))
  }, [])

  const handleSetClient = useCallback((e) => {
    setUser((prev) => ({ ...prev, client_id: e.target.value }))
  }, [])

  const isClientRole = !!user?.is_client_user

  // Self sign-up locations are capped on client users. Asked only when this form would add one;
  // the backend refuses at the cap regardless, this just says so before the click.
  const [clientQuota, setClientQuota] = useState(null)
  useEffect(() => {
    if (!isCreateNewUser || !isClientRole || !centerDetail?.id) {
      setClientQuota(null)
      return
    }
    let cancelled = false
    // Per body shop (0173): the chosen client's seats; before one is chosen, the plan's cap alone.
    getClientUserQuota({ ccid: centerDetail.id, clientId: user?.client_id || undefined }).then((quota) => {
      if (!cancelled) setClientQuota(quota || null)
    })
    return () => {
      cancelled = true
    }
  }, [isCreateNewUser, isClientRole, centerDetail?.id, user?.client_id])
  const clientCapReached = isCreateNewUser && isClientRole && !!clientQuota?.perClient && !!clientQuota?.exhausted
  // Without a prefetched list, load it as soon as the form opens rather than when Client is
  // clicked: a list arriving while the dropdown is already open makes it resize under the user.
  // The client tab never asks which client, so it needs no list.
  useEffect(() => {
    if (isClientForm || prefetchedClients || !centerDetail?.id) {
      return
    }
    let cancelled = false
    getClients({ ccid: centerDetail.id })
      .then((response) => {
        if (!cancelled && response?.status === 200) {
          setFetchedClients(response.data || [])
        }
      })
      .catch((error) => {
        Logger.error({ message: error, payload: { file: 'userForm', method: 'getClients', ccid: centerDetail.id } })
      })
    return () => {
      cancelled = true
    }
  }, [isClientForm, prefetchedClients, centerDetail?.id])

  const handleSaveUser = useCallback(
    async (override) => {
      if (user.is_admin && touched.role && !override) {
        setShowModal(true)
        return
      }

      setLoading(true)
      try {
        const updatedUserDetails = Object.fromEntries(
          Object.entries(user).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
        )

        setUser(updatedUserDetails)
        const data = {
          calibration_center_id: centerDetail.id,
          ...updatedUserDetails
        }

        const saveUserResponse = isEdit
          ? await updateUser({ data })
          : isCreateNewUser
            ? await createNewUser({ data })
            : await AddExistingUser({ data })
        if (saveUserResponse && (saveUserResponse.status === 200 || saveUserResponse.status === 201)) {
          close()
          onSaveSuccess(data)
        }
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'userForm', method: 'handleSaveUser', newUser: user } })
        setApiError(extractApiError(error) || 'Failed to save user. Please try again.')
      } finally {
        setLoading(false)
      }
    },
    [centerDetail?.id, close, isEdit, onSaveSuccess, setLoading, touched.role, user]
  )

  const handleDeleteUser = useCallback(async () => {
    setApiError(null)
    try {
      const role = userData.is_manager ? 'manage' : userData.is_owner ? 'owns' : 'tech'
      const response = await deleteUser({ userId: user.id, role, ccId: centerDetail.id })
      if (response?.status === 200) {
        close()
        onDeleteSuccess()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'userForm', method: 'handleDeleteUser', userId: user?.id } })
      setApiError(extractApiError(error) || 'Failed to delete user. Please try again.')
    }
  }, [user, userData, centerDetail.id, close, onDeleteSuccess])

  const [currentUserRole, targetUserRole] = useMemo(() => {
    // getRole is the one derivation that knows about every role, including client;
    // ROLES_HIERARCHY holds the lower-case names checkPermission compares.
    return [getRole(currentUser).toLowerCase(), getRole(userData).toLowerCase()]
  }, [currentUser, user])

  const modalContinue = useCallback(
    (code) => {
      if (code === '2180') {
        setShowModal(false)
        handleSaveUser(true)
      } else {
        setShowModal(true)
      }
    },
    [handleSaveUser]
  )

  const handleAddUserToggle = useCallback((e) => {
    const isAddNewUser = e.target.value === 'true'
    if (!isAddNewUser) {
      setUser((prev) => ({
        ...prev,
        name: '',
        phone: '',
        is_active: true,
        is_admin: false,
        is_owner: false,
        is_manager: false
      }))
    }
    setTouched((prev) => ({
      ...prev,
      name: false,
      phone: false
    }))

    setValid((prev) => ({
      ...prev,
      name: validations.name(''),
      phone: validations.phone('')
    }))

    setIsCreateNewUser(isAddNewUser)
  }, [])

  // A client user must be linked to a body shop - the backend refuses one without it, and
  // it would see nothing - so the form will not submit until one is picked.
  const isMissingClient = isCreateNewUser && isClientRole && !user?.client_id
  const isDisable = isCreateNewUser ? !valid.name || !valid.email || !valid.phone || isMissingClient || clientCapReached : !valid.email
  const titleAction = isEdit ? 'Edit' : 'Add'
  const userIdLabel = user?.id ? `#${user.id}` : ''
  const eyebrow = (
    <>
      STORE {' · '} USERS
      {userIdLabel && (
        <>
          {' · '}
          {userIdLabel}
        </>
      )}
    </>
  )

  const footer = (
    <AdasBox className='flex items-center justify-between gap-3'>
      <div>
        {allowDelete && !isAddUser && checkPermission({ currentUserRole, targetUserRole }) && (
          <button
            type='button'
            onClick={() => setShowDeleteModal(true)}
            className='inline-flex items-center justify-center rounded-md border border-[#FCA5A5] bg-white px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-[#DC2626] transition-colors hover:bg-[#FEF2F2]'
          >
            Delete
          </button>
        )}
      </div>
      <div className='flex items-center gap-3'>
        <button
          type='button'
          onClick={close}
          className='inline-flex items-center justify-center rounded-md border border-[#D1D5D8] px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
        >
          Cancel
        </button>
        <button
          type='submit'
          onClick={() => handleSaveUser(false)}
          disabled={isDisable}
          className='inline-flex items-center justify-center rounded-md bg-[#0A1628] px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-white transition-colors hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-50'
        >
          {isEdit ? 'Save Changes' : 'Add User'}
        </button>
      </div>
    </AdasBox>
  )

  return (
    <FormModal
      open={open}
      onClose={close}
      title={`${titleAction} User`}
      icon={UserIcon}
      eyebrow={eyebrow}
      footer={footer}
      errorMessage={apiError}
      onDismissError={() => setApiError(null)}
    >
      <div>
        {!isEdit && (
          <AdasFormControl>
            <AdasFormLabel className='mt-[10px] mb-0.5'>Access Type</AdasFormLabel>
            <AdasRadioGroup
              row
              options={[
                { label: 'New User', value: true },
                { label: 'Existing', value: false }
              ]}
              className='mb-[10px] pl-[5px]'
              aria-labelledby='type-group'
              name='type'
              value={isCreateNewUser}
              onChange={handleAddUserToggle}
            />
          </AdasFormControl>
        )}
        {isCreateNewUser && (
          <AdasTextField
            onChange={handleInputChange}
            name='name'
            className='mb-[20px] mt-[10px]'
            id='name'
            label='Name *'
            variant='outlined'
            value={user && user.name ? user.name : ''}
            error={touched.name && !valid.name}
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 40
            }}
            disabled={global}
          />
        )}
        <AdasTextField
          type='email'
          onChange={handleInputChange}
          name='email'
          className='mb-[20px]'
          id='email'
          label='Email *'
          variant='outlined'
          value={user && user.email ? user.email : ''}
          error={touched.email && !valid.email}
          helperText={touched.email && !valid.email ? TEXT_CONSTANTS.ENTER_VALID_EMAIL : ''}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            maxLength: 70
          }}
          disabled={global}
        />
        {isCreateNewUser && (
          <AdasTextField
            onChange={handleInputChange}
            name='phone'
            className='mb-[20px]'
            id='phone'
            label='Phone *'
            variant='outlined'
            value={user && user.phone ? phoneNumberDisplay(user.phone) : ''}
            error={touched.phone && !valid.phone}
            fullWidth={true}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 14
            }}
            disabled={global}
          />
        )}
        {isCreateNewUser && (
          <>
            <div className='mb-4 flex flex-col gap-2'>
              <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Status</span>
              <div className='inline-flex items-center gap-2'>
                <SegmentedButton active={!!user?.is_active} onClick={() => handleSetActive(true)} disabled={global}>
                  Active
                </SegmentedButton>
                <SegmentedButton active={user && !user.is_active} onClick={() => handleSetActive(false)} disabled={global}>
                  Inactive
                </SegmentedButton>
              </div>
            </div>
            {!isClientForm && (
              <div className='mb-5 flex flex-col gap-2'>
                <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Role</span>
                <div className='inline-flex flex-wrap items-center gap-2'>
                  {currentUser.is_admin && (
                    <SegmentedButton active={getRole(user) === 'Admin'} onClick={() => handleSetRole('Admin')} disabled={global}>
                      Admin
                    </SegmentedButton>
                  )}
                  {(currentUser.is_admin || (currentUser.is_owner && user.id === currentUser.id)) && (
                    <SegmentedButton active={getRole(user) === 'Owner'} onClick={() => handleSetRole('Owner')} disabled={global}>
                      Owner
                    </SegmentedButton>
                  )}
                  <SegmentedButton active={getRole(user) === 'Manager'} onClick={() => handleSetRole('Manager')} disabled={global}>
                    Manager
                  </SegmentedButton>
                  <SegmentedButton active={getRole(user) === 'Tech'} onClick={() => handleSetRole('Tech')} disabled={global}>
                    Tech
                  </SegmentedButton>
                  <SegmentedButton active={getRole(user) === 'Client'} onClick={() => handleSetRole('Client')} disabled={global}>
                    Client
                  </SegmentedButton>
                </div>
              </div>
            )}
            {isClientRole && clientQuota?.limited && (
              <div
                role='note'
                className={`mb-5 rounded-md border px-3 py-2 text-[12px] ${
                  clientCapReached ? 'border-[#F5C2C2] bg-[#FDECEC] text-[#7A271A]' : 'border-[#D1D5D8] bg-[#F8FAFC] text-[#6B7280]'
                }`}
              >
                {clientCapReached
                  ? `This body shop can have up to ${clientQuota.limit} client users and already has ${clientQuota.used}. Deactivate one to add another.`
                  : clientQuota.perClient
                    ? `${clientQuota.used} of ${clientQuota.limit} client users in use for this body shop.`
                    : `Up to ${clientQuota.limit} client users per body shop on the ${clientQuota.tierName || 'current'} plan.`}
              </div>
            )}
            {isClientRole && !isClientForm && (
              <div className='mb-5 flex flex-col gap-2'>
                <span className='text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]'>Client *</span>
                <AdasSelect
                  name='client_id'
                  value={user?.client_id}
                  onChange={handleSetClient}
                  options={clients.map((client) => ({ id: client.id, name: client.name }))}
                  placeholder='Select the client this user belongs to'
                  disabled={global}
                />
                <span className='text-[12px] text-[#6B7280]'>
                  They will only see this client&apos;s quotes, work orders, invoices and AI reports.
                </span>
              </div>
            )}
          </>
        )}
      </div>
      <ChallengeModal open={showModal} handleClose={() => setShowModal(false)} handleContinue={modalContinue} />
      <ConfirmModal
        cancelLabel={BUTTONS_DISPLAY_LABEL.CANCEL}
        confirmLabel={BUTTONS_DISPLAY_LABEL.CONFIRM}
        open={showDeleteModal}
        title={GENERIC_MESSAGES.DELETE_USER}
        message={GENERIC_MESSAGES.DELETE_USER_MESSAGE}
        handleClose={() => setShowDeleteModal(false)}
        handleContinue={handleDeleteUser}
      />
    </FormModal>
  )
}

UserForm.propTypes = {
  open: PropTypes.bool,
  userData: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  global: PropTypes.bool,
  allowDelete: PropTypes.bool,
  isAddUser: PropTypes.bool,
  onDeleteSuccess: PropTypes.func,
  userKind: PropTypes.oneOf(['client']),
  clients: PropTypes.array
}

ChallengeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleContinue: PropTypes.func.isRequired
}

const SegmentedButton = ({ active, children, onClick, disabled, type = 'button' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`px-5 py-1.5 text-[12px] font-semibold uppercase tracking-wide rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      active ? 'bg-[#E8EFFF] text-[#1F6FEB] border border-[#1F6FEB]' : 'bg-white text-[#6B7280] border border-[#D1D5D8] hover:bg-[#F8FAFC]'
    }`}
  >
    {children}
  </button>
)

SegmentedButton.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  type: PropTypes.string
}
